// 20261020: whose local cache (IndexedDB "chat.2s" + in-memory mirrors) is in use.
// Cache keys were not per user and nothing was cleared on logout, so the next user on the same device could see the
// previous user's cached Workplace/Task/chat content. Every cache key now starts with `u{id}.` (id = the logged-in
// user), and logout clears the caches. View-As mode uses no cache at all (scope = null, user decision 2026-10-20).
// No imports on purpose: utils.ts (logout) and dbUtils.ts (cache) both use this; they import each other's module
// the other way round already.
const LS_KEY = "dbScopeId";
let scopeId: number | null = null;
let loaded = false;
// handler gets the key prefix to KEEP (null = keep nothing)
const clearHandlers: Array<(keepPrefix: string | null) => Promise<void> | void> = [];

const runClear = async (keepPrefix: string | null) => {
  for (const fn of clearHandlers) {
    try { await fn(keepPrefix); } catch (e) { console.error(e); }
  }
};

export const DbScope = {
  // called with the user from /me (null in View-As mode)
  set(id: number | null | undefined): void {
    const prev = DbScope.get();
    scopeId = id ? Number(id) : null;
    loaded = true;
    try {
      if (scopeId) localStorage.setItem(LS_KEY, String(scopeId));
      else localStorage.removeItem(LS_KEY);
    } catch (e) { }
    // another user than the last one on this device (logged in without logout, or the logout clear was cut off by
    // a page redirect) -> drop the other users' caches, keep this user's
    if (scopeId && prev && prev !== scopeId) runClear(`u${scopeId}.`);
  },

  // last known user, also before /me answers at startup (lets the startup paint use this user's cache)
  get(): number | null {
    if (!loaded) {
      loaded = true;
      try {
        const v = localStorage.getItem(LS_KEY);
        scopeId = v ? Number(v) : null;
      } catch (e) {
        scopeId = null;
      }
    }
    return scopeId;
  },

  // key prefix of the current user, null = no user (do not read or write any cache)
  prefix(): string | null {
    const id = DbScope.get();
    return id ? `u${id}.` : null;
  },

  onClear(fn: (keepPrefix: string | null) => Promise<void> | void): void {
    clearHandlers.push(fn);
  },

  // logout: forget the user and delete the caches
  async clear(): Promise<void> {
    DbScope.set(null);
    await runClear(null);
  },

  // 20261022: Settings "clear cache" button - delete every user's caches on this device, stay logged in
  async clearCaches(): Promise<void> {
    await runClear(null);
  },
};
